import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import CommonStyles from "@components/CommonStyles";
import useToggle from "@base/hooks/useToggle";
import { IDeliveryAddress } from "@base/modules/customerDeliveryAddress/customerDeliveryAddress.interface";
import customerDeliveryAddressService from "@base/modules/customerDeliveryAddress/customerDeliveryAddressService";
import { IPatientRecord } from "@base/modules/user/profile/profile.interface";
import CustomerAddressForm from "./CustomerAddressForm";
import SelectionTrigger from "./SelectionTrigger";

/** `"<house number, street>, <ward>, <province>"`, skipping whatever is missing. */
export const formatDeliveryAddress = (address?: IDeliveryAddress) => {
  return [address?.customer_delivery_address, address?.customer_delivery_ward_name, address?.customer_delivery_province_name]
    .filter(Boolean)
    .join(", ");
};

interface IProps {
  label: string;
  error?: string;
  patientRecord?: IPatientRecord;
  selected?: IDeliveryAddress;
  /** Set when the patient has just switched service: the address is what they choose next. */
  autoOpen?: boolean;
  onAutoOpened?: () => void;
  onSelect: (address: IDeliveryAddress) => void;
}

/**
 * The service address for a home visit. The addresses come from the patient's own address
 * book rather than being typed per booking, so the same address can be reused and corrected once.
 */
export default function CustomerAddressPicker({ label, error, patientRecord, selected, autoOpen, onAutoOpened, onSelect }: IProps) {
  //! state
  const { t } = useTranslation();
  const { open: openForm, shouldRender: shouldRenderForm, toggle: toggleForm } = useToggle();
  const { open: openList, shouldRender: shouldRenderList, toggle: toggleList } = useToggle();

  const [addresses, setAddresses] = useState<IDeliveryAddress[]>([]);
  const [isLoaded, setIsLoaded] = useState<boolean>(false);
  const [editing, setEditing] = useState<IDeliveryAddress>();

  // `onSelect` changes whenever the parent's doctor list does. Reading it through a ref keeps the
  // address book from reloading - and snapping back to the primary address - on every such change.
  const onSelectRef = useRef(onSelect);

  useEffect(() => {
    onSelectRef.current = onSelect;
  }, [onSelect]);

  //! function
  const loadAddresses = useCallback(async (preferId?: string | number) => {
    try {
      const resp = await customerDeliveryAddressService.getMyAddresses();
      const list = resp.data ?? [];

      setAddresses(list);

      // Nothing is chosen for the patient (§12.8); only an address just saved becomes the choice.
      const saved = preferId ? list.find((address) => address.id === preferId) : undefined;

      if (saved) {
        onSelectRef.current(saved);
      }
    } catch (error) {
    } finally {
      setIsLoaded(true);
    }
  }, []);

  useEffect(() => {
    loadAddresses();
  }, [loadAddresses]);

  // Waits for the address book, since an empty one has nothing to pick and goes to the form instead.
  useEffect(() => {
    if (!autoOpen || !isLoaded) {
      return;
    }

    onAutoOpened?.();

    if (addresses.length) {
      if (!openList) {
        toggleList();
      }
    } else if (!openForm) {
      setEditing(undefined);
      toggleForm();
    }
  }, [autoOpen, isLoaded, addresses.length, openList, openForm, toggleList, toggleForm, onAutoOpened]);

  const onSaved = (saved: IDeliveryAddress) => {
    toggleForm();
    setEditing(undefined);
    loadAddresses(saved.id);

    // Saving from the list is a choice too, so the list has done its job.
    if (openList) {
      toggleList();
    }
  };

  //! render
  // The list opens on the address in use, else the primary one, else the first.
  const highlightedId = selected?.id ?? (addresses.find((address) => address.is_primary || address.primary) ?? addresses[0])?.id;

  return (
    <div className="flex flex-col gap-2">
      <div className="flex justify-between items-center gap-2">
        <span className="text-common-maintext text-sm font-medium font-['Inter']">
          {label} <span className="text-common-red500">*</span>
        </span>

        <CommonStyles.ButtonUI
          size="small"
          ghost
          type="primary"
          onClick={() => {
            setEditing(undefined);
            toggleForm();
          }}
        >
          {t("packageBooking.addAddress")}
        </CommonStyles.ButtonUI>
      </div>

      <SelectionTrigger
        title={selected ? formatDeliveryAddress(selected) : undefined}
        placeholder={addresses.length ? t("packageBooking.chooseAddress") : t("packageBooking.emptyAddress")}
        hasError={!!error}
        onClick={toggleList}
      />

      {error ? <span className="text-common-red500 text-xs font-normal font-['Inter']">{error}</span> : null}

      {shouldRenderList && (
        <CommonStyles.ModalConfirmationUI
          open={openList}
          toggle={toggleList}
          type="normal"
          hiddenAction
          title={label}
          content={
            <div className="flex flex-col gap-2 max-h-[360px] overflow-y-auto">
              {addresses.map((address) => {
                const isActive = address.id === highlightedId;

                return (
                  <div
                    key={address.id}
                    onClick={() => {
                      onSelect(address);
                      toggleList();
                    }}
                    className={`p-3 rounded-lg border cursor-pointer flex justify-between items-start gap-2 ${
                      isActive ? "border-common-primary bg-common-primary100" : "border-common-divider bg-common-white"
                    }`}
                  >
                    {/* The booking is already for this record, so its name and phone add nothing here. */}
                    <CommonStyles.TypographyUI use="paragraph" className="text-common-maintext text-sm font-normal font-['Inter'] !mb-0">
                      {formatDeliveryAddress(address)}
                    </CommonStyles.TypographyUI>

                    <CommonStyles.ButtonUI
                      size="small"
                      type="link"
                      onClick={(event) => {
                        event.stopPropagation();
                        setEditing(address);
                        toggleForm();
                      }}
                    >
                      {t("packageBooking.editAddress")}
                    </CommonStyles.ButtonUI>
                  </div>
                );
              })}

              {!addresses.length ? (
                <div className="w-full bg-common-background rounded-lg p-3 text-center text-common-subtext text-sm font-normal font-['Inter']">
                  {t("packageBooking.emptyAddress")}
                </div>
              ) : null}
            </div>
          }
        />
      )}

      {shouldRenderForm && (
        <CustomerAddressForm
          open={openForm}
          toggle={toggleForm}
          address={editing}
          patientRecord={patientRecord}
          isFirstAddress={addresses.length === 0}
          onSaved={onSaved}
        />
      )}
    </div>
  );
}
